import type { SpecializationCount } from '../services/professional-public.service';

export interface FilterValues {
  specialization?: string;
  minRate?: number;
  maxRate?: number;
  available?: boolean;
}

interface FilterSidebarProps {
  filters: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
  onReset: () => void;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  /** When provided, renders a specialization radio list at the top. */
  specializations?: SpecializationCount[];
}

function hasActiveFilters(filters: FilterValues): boolean {
  return !!(
    filters.specialization ||
    filters.minRate != null ||
    filters.maxRate != null ||
    filters.available
  );
}

/** Mobile toggle button — render this wherever you want the trigger to appear. */
export function FilterMobileToggle({
  filters,
  onClick,
}: {
  filters: FilterValues;
  onClick: () => void;
}) {
  const active = hasActiveFilters(filters);
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 lg:hidden"
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
      </svg>
      Filtri
      {active && (
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-orange text-[10px] font-bold text-white">
          !
        </span>
      )}
    </button>
  );
}

export function FilterSidebar({
  filters,
  onFilterChange,
  onReset,
  mobileOpen,
  onMobileOpenChange,
  specializations,
}: FilterSidebarProps) {
  const activeFilters = hasActiveFilters(filters);

  const content = (
    <div className="space-y-6">
      {/* Specializzazioni (optional) */}
      {specializations && specializations.length > 0 && (
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-400">
            Specializzazioni
          </h3>
          <div className="space-y-1">
            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-neutral-100">
              <input
                type="radio"
                name="specialization"
                checked={!filters.specialization}
                onChange={() => onFilterChange({ ...filters, specialization: undefined })}
                className="h-3.5 w-3.5 accent-brand-orange"
              />
              <span className={!filters.specialization ? 'font-medium text-brand-orange' : 'text-neutral-600'}>
                Tutte
              </span>
            </label>
            {specializations.map((spec) => (
              <label
                key={spec.name}
                className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                  spec.count === 0 ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-neutral-100'
                }`}
              >
                <input
                  type="radio"
                  name="specialization"
                  disabled={spec.count === 0}
                  checked={filters.specialization === spec.name}
                  onChange={() => onFilterChange({ ...filters, specialization: spec.name })}
                  className="h-3.5 w-3.5 accent-brand-orange"
                />
                <span className={filters.specialization === spec.name ? 'font-medium text-brand-orange' : 'text-neutral-600'}>
                  {spec.name}
                </span>
                <span className="ml-auto text-xs text-neutral-300">{spec.count}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Prezzo */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-400">
          Prezzo (EUR/min)
        </h3>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            step={0.1}
            placeholder="Min"
            value={filters.minRate ?? ''}
            onChange={(e) => {
              const val = e.target.value ? parseFloat(e.target.value) : undefined;
              onFilterChange({ ...filters, minRate: val });
            }}
            className="h-9 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder-neutral-400 outline-none focus:border-brand-teal/40"
          />
          <span className="shrink-0 text-xs text-neutral-300">-</span>
          <input
            type="number"
            min={0}
            step={0.1}
            placeholder="Max"
            value={filters.maxRate ?? ''}
            onChange={(e) => {
              const val = e.target.value ? parseFloat(e.target.value) : undefined;
              onFilterChange({ ...filters, maxRate: val });
            }}
            className="h-9 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder-neutral-400 outline-none focus:border-brand-teal/40"
          />
        </div>
      </div>

      {/* Disponibilita */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-400">
          Disponibilita
        </h3>
        <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-neutral-100">
          <input
            type="checkbox"
            checked={filters.available ?? false}
            onChange={(e) => onFilterChange({ ...filters, available: e.target.checked || undefined })}
            className="h-4 w-4 rounded accent-brand-teal"
          />
          <span className={filters.available ? 'font-medium text-emerald-600' : 'text-neutral-600'}>
            Solo disponibili ora
          </span>
        </label>
      </div>

      {/* Reset */}
      {activeFilters && (
        <button
          onClick={onReset}
          className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-900"
        >
          Cancella filtri
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar — hidden below lg, no layout impact on mobile */}
      <aside className="hidden w-60 shrink-0 lg:block">
        <div className="sticky top-20 rounded-xl border border-neutral-200 bg-white/60 p-4">
          {content}
        </div>
      </aside>

      {/* Mobile filter bottom sheet panel */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => onMobileOpenChange(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-neutral-200 bg-white p-4 shadow-xl lg:hidden">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-brand-navy">Filtri</h2>
              <button
                onClick={() => onMobileOpenChange(false)}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {content}
          </div>
        </>
      )}
    </>
  );
}
